import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import Table from '@amzn/awsui-components-react/polaris/table';
import Box from '@amzn/awsui-components-react/polaris/box';
import Link from '@amzn/awsui-components-react/polaris/link';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import { SpaceBetween, TableProps, Toggle } from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { EVENT_DETAILS_ROUTES, CHALLENGE_DETAILS_ROUTES } from '@/src/routes';
import { AggregatedEventRow, getStatusColumns } from './dailySummaryModal.utils';
import { getLanguageCodeSafe } from '@/src/utils/locale.utils';

export interface DailySummaryModalProps {
  selectedDate: Date;
  events: AggregatedEventRow[];
}

/**
 * Modal component that displays a daily summary of events for a selected date.
 * Shows a table with one row per event and dynamic status columns derived from
 * the aggregated lab summary data. Rows are expandable to show per-challenge breakdown.
 */
const DailySummaryModal = NiceModal.create(({ selectedDate, events }: DailySummaryModalProps) => {
  const modal = useModal();
  const { t, i18n } = useTranslation();
  const languageCode = getLanguageCodeSafe(i18n.language);
  const [hideCampaignEvents, setHideCampaignEvents] = useState(false);
  const [expandedItems, setExpandedItems] = useState<AggregatedEventRow[]>([]);

  const filteredEvents = useMemo(
    () => (hideCampaignEvents ? events.filter((event) => !event.campaignEvent) : events),
    [events, hideCampaignEvents]
  );

  const { items, paginationProps } = useCollection(filteredEvents, {
    pagination: { pageSize: 15 },
    sorting: {},
  });

  const statusColumns = useMemo(() => getStatusColumns(filteredEvents), [filteredEvents]);

  const columnDefinitions = useMemo(() => {
    const columns: TableProps.ColumnDefinition<AggregatedEventRow>[] = [
      {
        id: 'eventTitle',
        header: t(i18nKeys.labDashboard.calendar.dailySummaryModal.eventNameHeader),
        cell: (item) =>
          item.challengeId ? (
            <Link target="_blank" href={CHALLENGE_DETAILS_ROUTES.Summary.resolve(item.challengeId)}>
              {item.challengeId}
            </Link>
          ) : (
            <Link target="_blank" href={EVENT_DETAILS_ROUTES.Labs.resolve(item.eventName)}>
              {item.eventTitle}
            </Link>
          ),
      },
      ...(!hideCampaignEvents
        ? [
            {
              id: 'campaignEvent',
              header: t(i18nKeys.labDashboard.calendar.dailySummaryModal.campaignEventHeader),
              cell: (item: AggregatedEventRow) =>
                item.campaignEvent ? t(i18nKeys.general.yes) : t(i18nKeys.general.no),
            },
          ]
        : []),
      ...statusColumns.map((status) => ({
        id: status,
        header: t(`labDashboard.calendar.dailySummaryModal.labStatus.${status}`, status),
        cell: (item: AggregatedEventRow) => item.statusCounts[status] ?? 0,
      })),
    ];
    return columns;
  }, [statusColumns, t, hideCampaignEvents]);

  const formattedDate = selectedDate.toLocaleDateString(languageCode);

  return (
    <Modal
      visible={modal.visible}
      data-testid="daily-summary-modal"
      onDismiss={() => {
        modal.resolve();
        modal.hide();
      }}
      header={t(i18nKeys.labDashboard.calendar.dailySummaryModal.header, { date: formattedDate })}
      size="large"
    >
      <SpaceBetween size="m">
        <Box variant="p">{t(i18nKeys.labDashboard.calendar.dailySummaryModal.description)}</Box>
        <Toggle
          checked={hideCampaignEvents}
          data-testid="daily-summary-modal__campaign-events-toggle"
          onChange={({ detail }) => setHideCampaignEvents(detail.checked)}
        >
          {t(i18nKeys.labDashboard.calendar.dailySummaryModal.hideCampaignEvents)}
        </Toggle>
        <Table
          items={items}
          columnDefinitions={columnDefinitions}
          data-testid="daily-summary-modal__events-table"
          variant="embedded"
          pagination={<Pagination {...paginationProps} />}
          expandableRows={{
            getItemChildren: (item) =>
              Object.entries(item.challengeBreakdown).map(([challengeId, statusMap]) => ({
                eventName: item.eventName,
                eventTitle: challengeId,
                campaignEvent: item.campaignEvent,
                statusCounts: statusMap,
                challengeBreakdown: {},
                challengeId,
              })),
            isItemExpandable: (item) => Object.keys(item.challengeBreakdown).length > 0,
            expandedItems,
            onExpandableItemToggle: ({ detail }) => {
              const item = detail.item;
              const isExpanded = detail.expanded;
              setExpandedItems((prev) =>
                isExpanded ? [...prev, item] : prev.filter((i) => i.eventName !== item.eventName)
              );
            },
          }}
          empty={
            <Box textAlign="center" padding="l">
              {t(i18nKeys.labDashboard.calendar.dailySummaryModal.noEvents)}
            </Box>
          }
        />
      </SpaceBetween>
    </Modal>
  );
});

export default DailySummaryModal;
